import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import { EventOut, EventVenueOut, VenueTypeOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Input from '@/components/atoms/Input/Input';
import Icon from '@/components/atoms/Icon/Icon';
import ScrollableCategories from '@/components/molecules/ScrollableCategories/ScrollableCategories';
import './EventVenues.scss';
import RegistrationEncouragement from '@/components/molecules/RegistrationEncouragement';

const EventVenues: React.FC = () => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<EventOut | null>(null);
  const [venues, setVenues] = useState<EventVenueOut[]>([]);
  const [venueTypes, setVenueTypes] = useState<VenueTypeOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVenueType, setSelectedVenueType] = useState<number | null>(null);
  const [showRegistrationEncouragement, setShowRegistrationEncouragement] = useState(true);

  const getErrorMessage = useCallback((err: any) => {
    if (err.response?.status === 404) {
      return t('events.notFound');
    }
    return err.message || t('errors.general');
  }, [t]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId) return;
      
      try {
        setLoading(true);
        
        // Fetch event details, venues, and venue types in parallel
        const [eventData, venuesData, venueTypesData] = await Promise.all([
          apiService.getEvent(parseInt(eventId)),
          apiService.getAnonymousEventVenues(parseInt(eventId)),
          apiService.getVenueTypes()
        ]);
        
        setEvent(eventData);
        setVenues(venuesData);
        setVenueTypes(venueTypesData);
        
        // If no venues are available, redirect directly to event details
        // We'll use a default venue ID of 0 or handle it in the EventDetails page
        if (venuesData.length === 0) {
          console.log('No venues found for event, redirecting to event details');
          navigate(`/events/${eventId}/venues/0`, { replace: true });
        }
        
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, getErrorMessage, navigate]);

  const handleVenueSelect = useCallback((venueId: number) => {
    navigate(`/events/${eventId}/venues/${venueId}`);
  }, [navigate, eventId]);

  // Description truncation logic
  const MAX_DESCRIPTION_LENGTH = 100;
  const shouldTruncate = event?.description && event.description.length > MAX_DESCRIPTION_LENGTH;
  const displayDescription = shouldTruncate && !isDescriptionExpanded 
    ? event?.description?.substring(0, MAX_DESCRIPTION_LENGTH) + '...'
    : event?.description;

  const toggleDescription = useCallback(() => {
    setIsDescriptionExpanded(prev => !prev);
  }, []);

  // Helper function to get venue type name
  const getVenueTypeName = useCallback((typeId: number) => {
    return venueTypes.find(type => type.id === typeId)?.name || `Type ${typeId}`;
  }, [venueTypes]);

  // Get available venue types for filtering (only types that have venues)
  const availableVenueTypes = venueTypes.filter(type => 
    venues.some(venue => venue.type_id === type.id)
  );

  // Filter venues based on search term and venue type
  const filteredVenues = venues.filter(venue => {
    const matchesSearch = !searchTerm || 
      venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !selectedVenueType || venue.type_id === selectedVenueType;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/events')}
          >
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container">
        <div className="error">
          <p>{t('events.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="event-venues-page">
        <div className="event-venues-page__header">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/events')}
          >
            <Icon name="atras" size={20} />
          </Button>
          <h1 className="event-venues-page__title">{event.name}</h1>
        </div>

        <div className="event-venues-page__content">
          
          <div className="event-venues-page__venues">
            
            {/* Search Bar */}
            <div className="event-venues-page__search">
              <div className="event-venues-page__search-container">
                <Input
                  type="text"
                  placeholder={t('venues.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="event-venues-page__search-input"
                />
                <Icon className="event-venues-page__search-icon" name="lupa" size={20} alt={t('venues.search.placeholder')} />
              </div>
            </div>

            {/* Venue Type Filters */}
            {availableVenueTypes.length > 0 && (
              <ScrollableCategories
                categories={[
                  { id: null, name: t('venues.allTypes') },
                  ...availableVenueTypes.map(type => ({ id: type.id, name: type.name }))
                ]}
                selectedCategory={selectedVenueType}
                onCategorySelect={setSelectedVenueType}
                className="event-venues-page__filters"
              />
            )}
            
            {venues.length === 0 ? (
              <div className="event-venues-page__empty">
                <p>{t('venues.noVenuesAvailable')}</p>
              </div>
            ) : (
              <div className="event-venues-page__venues-list">
                {filteredVenues.map((venue) => (
                  <Card key={venue.id} variant="elevated" className="venue-card">
                    <div className="venue-card__content">
                      <div className="flex venue-card__info">
                        {/* Venue Image */}
                        <div className="venue-card__image">
                          {venue.venue_image ? (
                            <img 
                              src={venue.venue_image} 
                              alt={venue.name}
                              className="venue-card__image-img"
                            />
                          ) : (
                            <div className="venue-card__image-placeholder">
                              <Icon name="eventos" size={32} />
                            </div>
                          )}
                        </div>
                        <div className="venue-card__details-container">
                          <div className="venue-card__details">
                            <div className="venue-card__title-container">
                              <h3 className="venue-card__title">{venue.name}</h3>
                            </div>
                            
                            {venue.description && (
                              <p className="venue-card__description">{venue.description}</p>
                            )}
                            
                            <div className="venue-card__meta">
                              {venue.zone && (
                                <span className="venue-card__zone">
                                  <Icon name="ubicacion" size={14} />
                                  {venue.zone}
                                </span>
                              )}
                              
                              {/*venue.capacity && (
                                <span className="venue-card__capacity">
                                  <Icon name="usuario" size={14} />
                                  {venue.capacity} {t('venues.capacity')}
                                </span>
                              )*/}
                            </div>
                          </div>
                          <div className="venue-card__actions">
                            <Button 
                              fullWidth={true}
                              variant="primary" 
                              size="smm"
                              onClick={() => handleVenueSelect(venue.id)}
                              className="venue-card__select-btn"
                            >
                              <Icon name="cubiertos" size={20} />
                              {t('venues.menu')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {/* Registration Encouragement */}
            {showRegistrationEncouragement && (
              <RegistrationEncouragement 
                variant="compact"
                onSkip={() => setShowRegistrationEncouragement(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventVenues;
